import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { mapAPI } from "services/mapSlice";

import {
  PingMapWorkspaceBottomPanelId,
  PingMapWorkspaceRightPanelId,
  SourceKey,
  AOAResponseType,
  ShapeFilterType,
} from "@repo/ping-react-js";
import { OutlineData } from "src/ts-types/ApiTypes";
import { FIRE_PML_DEFAULT_MIN_DISTANCE_FEET, FIRE_PML_DEFAULT_RADIUS_MILES, FirePMLGroupingMethod, FIRE_PML_GROUPING_METHODS } from "@repo/ping-react-js";
import { PAGE_STATES, PageState } from "src/constants/GlobalConstants";

export type PingMapWorkspaceLeftPanelId =
  | null
  | "search"
  | "buildings"
  | "layers"
  | "legend"
  | "building-filters";

type PingMapWorkspaceInitialState = {
  /**
   * The ID of the currently active left panel.
   */
  activePanelLeft: PingMapWorkspaceLeftPanelId;

  /**
   * The ID of the currently active left panel.
   */
  activePanelRight: PingMapWorkspaceRightPanelId;

  /**
   * Booelan to check if right info panel is extended
   */
  rightPanelExtended: boolean;

  /**
   * The ID of the currently active bottom panel.
   */
  activePanelBottom: PingMapWorkspaceBottomPanelId;

  /**
   * The IDs of the buildings the user has selected. These will be highlighted
   * on the map, shown in the sidebar, etc.
   */
  focusedBuildingIds: string[] | null;

  /**
   * The dimensions of the shape filter the user has drawn on the map (if any).
   */
  shapeFilterDimensions: Record<string, number>;

  /**
   * User selected lag,lng on google maps, used to pull AOA data
   */
  selectedLatLng: Record<"lat" | "lng", number> | null;

  /**
   * If we request a file, which we previously requested
   * then don't reuse work
   */
  prevReferencedPLI: Array<string>;

  /**
   *  AOA response data
   */
  aoaResponse: null | AOAResponseType;

  /**
   * Enables tilt and 3D building columns for google maps
   */
  mode3D: boolean;

  /**
   * Enables H3 hex rendering. Hexes are flat in 2D mode and extruded in 3D mode.
   */
  hexView: boolean;

  /**
   * Flag to determine if the user is creating a shape filter. Used for polygonal and circular tools
   */
  creatingShapeFilterMode: { type: ShapeFilterType } | null;

  displacementMode: boolean;

  firePMLMode: boolean;

  firePMLMinDistanceFeet: number;

  firePMLRadiusMiles: number;

  /**
   * Enum choice for Fire PML grouping. `Amount Subject Radius` or `Min Proximity Distance`
   */
  firePMLGroupingMethod: FirePMLGroupingMethod;

  firePMLHoveredGroupIndex: number | null;

  showOriginalGeocodedPins: boolean;

  showStateLayer: boolean;

  outlineData: OutlineData | null;

  geocodingMode: boolean;

  finalizingShapeFilterMode: null;

  mouseTooltip: string | null;

  pageState: PageState;

  hasWebGLSupport: boolean;

  origLatLng: {
    lat: number;
    lng: number;
  } | null;

  globeMode: boolean;

  hideKeyboardShortcuts: boolean;
};

const initialState: PingMapWorkspaceInitialState = {
  activePanelLeft: null,
  activePanelRight: null,
  rightPanelExtended: true,
  activePanelBottom: null,
  focusedBuildingIds: null,
  shapeFilterDimensions: {},
  pageState: PAGE_STATES.MAP,
  selectedLatLng: null,
  aoaResponse: null,
  mode3D: false,
  hexView: false,
  creatingShapeFilterMode: null,
  finalizingShapeFilterMode: null,
  displacementMode: false,
  firePMLMode: false,
  firePMLMinDistanceFeet: FIRE_PML_DEFAULT_MIN_DISTANCE_FEET,
  firePMLRadiusMiles: FIRE_PML_DEFAULT_RADIUS_MILES,
  firePMLGroupingMethod: FIRE_PML_GROUPING_METHODS.MIN_DISTANCE,
  firePMLHoveredGroupIndex: null,
  showOriginalGeocodedPins: false,
  showStateLayer: false,
  outlineData: null,
  geocodingMode: false,
  mouseTooltip: null,
  prevReferencedPLI: [],
  hasWebGLSupport: false,
  origLatLng: null,
  globeMode: false,
  hideKeyboardShortcuts:
    localStorage.getItem("hideKeyboardShortcuts") !== "false",
};

const mapWorkspaceSlice = createSlice({
  name: "mapWorkspace",
  initialState,
  reducers: {
    setRightPanelExtended(state, action: PayloadAction<boolean>) {
      state.rightPanelExtended = action.payload;
    },
    setPrevReferencedPLI(state, action: PayloadAction<Array<string>>) {
      state.prevReferencedPLI = action.payload;
    },
    setActivePanelLeft(
      state,
      action: PayloadAction<PingMapWorkspaceLeftPanelId>,
    ) {
      state.activePanelLeft = action.payload;
    },
    setActivePanelRight(
      state,
      action: PayloadAction<PingMapWorkspaceRightPanelId>,
    ) {
      state.activePanelRight = action.payload;
    },
    setActivePanelBottom(
      state,
      action: PayloadAction<PingMapWorkspaceBottomPanelId>,
    ) {
      state.activePanelBottom = action.payload;
    },
    setFocusedBuildingIds(state, action: PayloadAction<string[] | null>) {
      state.focusedBuildingIds = action.payload;
    },
    setSelectedLatLng(
      state,
      action: PayloadAction<Record<"lat" | "lng", number> | null>,
    ) {
      state.selectedLatLng = action.payload;
    },
    setAoaResponse(state, action: PayloadAction<AOAResponseType | null>) {
      if (!action.payload) {
        state.aoaResponse = null;
      } else if (!state.aoaResponse) {
        state.aoaResponse = action.payload;
      } else {
        state.aoaResponse = {
          ...state.aoaResponse,
          id: action.payload.id,
          location_data: {
            ...state.aoaResponse.location_data,
            ...action.payload.location_data,
          },
        };
      }
    },
    resetAoASection(state, action: PayloadAction<SourceKey>) {
      if (state.aoaResponse) {
        state.aoaResponse = {
          ...state.aoaResponse,
          location_data: {
            ...state.aoaResponse.location_data,
            [action.payload]: {},
          },
        };
      } else {
        state.aoaResponse = {
          id: "",
          location_data: {
            [action.payload]: {},
          },
        };
      }
    },
    toggleDisplacementMode(state, action: PayloadAction<boolean>) {
      state.displacementMode =
        typeof action.payload === "boolean"
          ? action.payload
          : !state.displacementMode;
    },
    toggleFirePMLMode(state, action: PayloadAction<boolean>) {
      state.firePMLMode =
        typeof action.payload === "boolean"
          ? action.payload
          : !state.firePMLMode;
    },
    setFirePMLMinDistanceFeet(state, action: PayloadAction<number>) {
      state.firePMLMinDistanceFeet = Math.max(0, Math.round(action.payload));
    },
    setFirePMLRadiusMiles(state, action: PayloadAction<number>) {
      state.firePMLRadiusMiles = Math.max(0, Math.round(action.payload));
    },
    setFirePMLGroupingMethod(state, action: PayloadAction<FirePMLGroupingMethod>) {
      state.firePMLGroupingMethod = action.payload;
    },
    setFirePMLHoveredGroupIndex(state, action: PayloadAction<number | null>) {
      state.firePMLHoveredGroupIndex = action.payload;
    },
    setShowOriginalGeocodedPins(state, action: PayloadAction<boolean>) {
      state.showOriginalGeocodedPins = action.payload;
    },
    setShowStateLayer(state, action: PayloadAction<boolean>) {
      state.showStateLayer = action.payload;
    },
    setMode3D(state) {
      state.mode3D = !state.mode3D;
    },
    setHexView(state, action: PayloadAction<boolean>) {
      state.hexView = action.payload;
    },
    setHasWebGLSupport(state, action: PayloadAction<boolean>) {
      state.hasWebGLSupport = action.payload;
    },
    setCreatingShapeFilterMode(
      state,
      action: PayloadAction<{ type: ShapeFilterType } | null>,
    ) {
      state.creatingShapeFilterMode = action.payload;
    },
    setShapeFilterDimensions(
      state,
      action: PayloadAction<Record<string, any>>,
    ) {
      state.shapeFilterDimensions = action.payload;
    },
    setOutlineData(state, action: PayloadAction<OutlineData | null>) {
      state.outlineData = action.payload;
    },
    setGeocodingMode(state, action: PayloadAction<boolean>) {
      state.geocodingMode = action.payload;
    },
    setFinalizingShapeFilterMode(state, action: PayloadAction<any>) {
      state.finalizingShapeFilterMode = action.payload;
    },
    setMouseTooltip(state, action: PayloadAction<string | null>) {
      state.mouseTooltip = action.payload;
    },
    setPageState(state, action: PayloadAction<PageState>) {
      state.pageState = action.payload;
    },
    setGlobeMode(state, action: PayloadAction<boolean>) {
      state.globeMode = action.payload;
    },
    setHideKeyboardShortcuts(state, action: PayloadAction<boolean>) {
      state.hideKeyboardShortcuts = action.payload;
      localStorage.setItem("hideKeyboardShortcuts", action.payload.toString());
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      mapAPI.endpoints.getBuildingByPolicy.matchFulfilled,
      (state, action) => {
        const bldgOriginal = action.payload?.original;

        if (
          bldgOriginal &&
          "latitude" in bldgOriginal &&
          "longitude" in bldgOriginal
        ) {
          state.origLatLng = {
            lat: bldgOriginal.latitude,
            lng: bldgOriginal.longitude,
          };
        }
      },
    );
  },
});

const { actions, reducer } = mapWorkspaceSlice;

export const {
  toggleDisplacementMode,
  toggleFirePMLMode,
  setFirePMLMinDistanceFeet,
  setFirePMLRadiusMiles,
  setFirePMLGroupingMethod,
  setFirePMLHoveredGroupIndex,
  setShowOriginalGeocodedPins,
  setShowStateLayer,
  setSelectedLatLng,
  setFocusedBuildingIds,
  setActivePanelRight,
  setActivePanelLeft,
  setActivePanelBottom,
  setAoaResponse,
  setMode3D,
  setHexView,
  setCreatingShapeFilterMode,
  setShapeFilterDimensions,
  resetAoASection,
  setOutlineData,
  setGeocodingMode,
  setFinalizingShapeFilterMode,
  setMouseTooltip,
  setPageState,
  setHasWebGLSupport,
  setGlobeMode,
  setHideKeyboardShortcuts,
  setRightPanelExtended,
  setPrevReferencedPLI,
} = actions;

export default reducer;
